import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, RefreshCw } from 'lucide-react';

import TopSchoolCard from '../components/report/TopSchoolCard';
import BlurredSchoolCard from '../components/report/BlurredSchoolCard';
import RevealedSchoolCard from '../components/report/RevealedSchoolCard';
import UnlockButton from '../components/report/UnlockButton';
import UnlockBanner from '../components/report/UnlockBanner';
import {
  toPriorityLabel,
  isUnlocked as isUnlockedStorage,
  setUnlocked as setUnlockedStorage,
  teaserLine,
  nearMissReason,
} from '../lib/reportUtils';
import { track, EVENTS } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Header — child + current school + generated date.
const ReportHeader = ({ summary, createdAt, onBack }) => {
  const childName = summary?.child_name || 'Your child';
  const grade = summary?.grade;
  const currentSchool = summary?.current_school_name;
  const dateStr = createdAt
    ? new Date(createdAt).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  return (
    <header className="mb-8">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-[#6B6560] hover:text-[#2C2823] mb-5"
        data-testid="report-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </button>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#E85D35] mb-2">Personalised school report</p>
      <h1 className="text-3xl sm:text-4xl font-black text-[#2C2823] leading-tight">
        {childName === 'Your child' ? 'A report built for your child' : `A report built for ${childName}`}
      </h1>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-3 text-sm text-[#6B6560]">
        {currentSchool && <span>Currently at <strong className="text-[#2C2823]">{currentSchool}</strong></span>}
        {grade != null && <span>Year {grade}</span>}
        {dateStr && <span>Generated {dateStr}</span>}
      </div>
    </header>
  );
};

// Error state component with retry
const ErrorState = ({ message, onRetry }) => (
  <div className="rounded-2xl bg-[#FFF5EB] border border-[#E8DED3] p-6 text-center max-w-md mx-auto">
    <p className="text-[#2C2823] font-semibold mb-3">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-[#E85D35] text-[#E85D35] font-semibold hover:bg-[#E85D35] hover:text-white transition"
      >
        <RefreshCw className="w-4 h-4" /> Try again
      </button>
    )}
  </div>
);

const ParentReportPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [schoolsList, setSchoolsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [justUnlocked, setJustUnlocked] = useState(false);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/parent/report/${reportId}`),
        fetch(`${API_URL}/api/schools/list`),
      ]);
      if (!rRes.ok) throw new Error('Report not found.');
      const rData = await rRes.json();
      const sData = sRes.ok ? await sRes.json() : { schools: [] };
      setReport(rData.report);
      setSchoolsList(sData.schools || []);
    } catch (e) {
      setError(e.message || 'Unable to load report.');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadReport();
    if (reportId) setUnlocked(isUnlockedStorage(reportId));
  }, [loadReport, reportId]);

  // Fire report_view once per load
  useEffect(() => {
    if (report) track(EVENTS.REPORT_VIEW, {
      report_id: reportId,
      report_type: report.report_type,
      unlocked,
    });
  }, [report, reportId, unlocked]);

  const handleUnlock = () => {
    setUnlockedStorage(reportId);
    setUnlocked(true);
    setJustUnlocked(true);
    // Scroll to the top of the page so the parent sees the unlock banner
    // slide in + the #1 card's pulse animation. `smooth` keeps the motion
    // feeling celebratory rather than a jarring jump.
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  const reportData = report?.report_data || {};
  const summary = useMemo(
    () => ({
      ...(reportData.questionnaire_summary || {}),
      current_school_name: reportData.current_school_name,
    }),
    [reportData]
  );
  const topSchools = reportData.top_schools || [];
  const nearMisses = reportData.near_misses || [];
  const topSchool = topSchools[0];
  const otherTopSchools = topSchools.slice(1, 3);

  // The spec says the RM 4.99 basic payment happens before this page loads.
  // But older basic reports in our DB only contain 1 school. Use premium flag to know if #2/#3 data exists.
  const reportType = reportData.report_type;
  const hasGatedTopSchools = reportType === 'premium' && otherTopSchools.length > 0;
  const hasGatedNearMisses = reportType === 'premium' && nearMisses.length > 0;

  // Edge case: when the parent's filter combination only produced ≤2 matches
  // and zero near-misses, there is nothing meaningful to gate behind a Full
  // upgrade. Show every available school in full detail under the Basic
  // report — no upgrade prompt.
  const thinReport = topSchools.length <= 2 && nearMisses.length === 0;

  // Highlights tier: full payment (post-unlock) → all signals on every card.
  // Pre-unlock OR `report_type === 'basic'` → exam-only on the top match,
  // nothing on revealed cards (which themselves sit behind the paywall).
  // Thin-report edge case → full signals on every card (nothing to upsell).
  const highlightsTier =
    thinReport || (reportType === 'premium' && unlocked) ? 'full' : 'basic';

  // ------- Render -------
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FEFDF8] flex items-center justify-center">
        <p className="text-[#6B6560]" data-testid="report-loading">Loading your report…</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-[#FEFDF8] flex items-center justify-center p-6">
        <ErrorState message={error || 'Report not found.'} onRetry={loadReport} />
      </div>
    );
  }

  if (!topSchool) {
    return (
      <div className="min-h-screen bg-[#FEFDF8] p-6 pt-12">
        <div className="max-w-3xl mx-auto">
          <ReportHeader summary={summary} createdAt={report.created_at} onBack={() => navigate('/parent-dashboard')} />
          <div className="rounded-2xl bg-[#FFF5EB] border border-[#E8DED3] p-8 text-center">
            <h3 className="text-xl font-bold text-[#2C2823] mb-2">No matches yet</h3>
            <p className="text-[#6B6560]">
              Your questionnaire didn't produce any matches with the current filters.
              Try broadening your budget band or location preferences and re-run the questionnaire.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FEFDF8] pb-28 sm:pb-16">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 pt-10 sm:pt-14">
        <ReportHeader summary={summary} createdAt={report.created_at} onBack={() => navigate('/parent-dashboard')} />

        {/* Post-upgrade celebratory banner — auto-dismisses after 8s */}
        <UnlockBanner
          show={justUnlocked}
          onDismiss={() => setJustUnlocked(false)}
        />

        {/* #1 school */}
        <TopSchoolCard
          school={topSchool}
          currentGrade={summary.grade}
          currentSchoolName={summary.current_school_name}
          currentSchoolFee={reportData.current_school_fee}
          schoolsList={schoolsList}
          summary={summary}
          apiUrl={API_URL}
          priorityLabels={(summary.priorities_ranked || []).slice(0, 3).map(toPriorityLabel).filter(Boolean)}
          reportId={reportId}
          highlightsTier={highlightsTier}
          justUnlocked={justUnlocked}
        />

        {/* Unlock button (inline, desktop) + sticky mobile */}
        {!unlocked && !thinReport && (hasGatedTopSchools || hasGatedNearMisses) && (
          <>
            <div className="mt-8 hidden sm:block">
              <UnlockButton reportId={reportId} onUnlock={handleUnlock} />
            </div>
            {/* Simple mobile CTA in flow + sticky bar */}
            <div className="mt-8 sm:hidden">
              <UnlockButton reportId={reportId} onUnlock={handleUnlock} />
            </div>
            <UnlockButton reportId={reportId} sticky onUnlock={handleUnlock} />
          </>
        )}

        {/* #2 (and #3) schools — when the report has ≤2 matches with no
            near-misses, surface the second match in full immediately rather
            than gating it behind an upgrade. */}
        {thinReport && otherTopSchools.length > 0 && (
          <section className="mt-10" data-testid="thin-report-extra-section">
            <h2 className="text-lg font-bold text-[#2C2823] mb-4">
              {otherTopSchools.length === 1 ? 'Your other strong match' : 'Your other strong matches'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {otherTopSchools.map((s, i) => (
                <RevealedSchoolCard
                  key={s.id || i}
                  school={s}
                  rank={i + 2}
                  currentGrade={summary.grade}
                  neutralReason={null}
                  reportId={reportId}
                  apiUrl={API_URL}
                  summary={summary}
                  highlightsTier="full"
                />
              ))}
            </div>
          </section>
        )}

        {/* #2 and #3 schools (gated/upgrade flow) */}
        {!thinReport && hasGatedTopSchools && (
          <section className="mt-10" data-testid="other-top-section">
            <h2 className="text-lg font-bold text-[#2C2823] mb-1">
              {unlocked ? 'Two more strong matches' : '2 more strong matches'}
            </h2>
            <p className="text-sm text-[#6B6560] mb-4">
              {unlocked ? 'Revealed after your upgrade.' : 'Unlock to see names, curricula, and fee ranges.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {otherTopSchools.map((s, i) => (
                unlocked ? (
                  <RevealedSchoolCard
                    key={s.id || i}
                    school={s}
                    rank={i + 2}
                    currentGrade={summary.grade}
                    neutralReason={null}
                    reportId={reportId}
                    apiUrl={API_URL}
                    summary={summary}
                    highlightsTier="full"
                  />
                ) : (
                  <BlurredSchoolCard
                    key={s.id || i}
                    index={i + 2}
                    teaser={teaserLine(s, i, summary.location_preference)}
                  />
                )
              ))}
            </div>
          </section>
        )}

        {/* Near-misses */}
        {hasGatedNearMisses && (
          <section className="mt-10" data-testid="near-misses-section">
            <h2 className="text-lg font-bold text-[#2C2823] mb-1">
              {topSchools.length < 3
                ? 'Closest matches — outside one of your filters'
                : unlocked ? 'Near-miss options worth a look' : 'Near-miss schools'}
            </h2>
            <p className="text-sm text-[#6B6560] mb-4">
              {topSchools.length < 3
                ? "These schools didn't pass every hard filter (budget, curriculum or location), but they're your closest options given how few schools match your exact profile."
                : unlocked
                  ? "Not a perfect fit against your priorities, but worth considering."
                  : 'Schools that just missed your top 3 — unlock to see what they are.'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {nearMisses.slice(0, 3).map((s, i) => (
                unlocked ? (
                  <RevealedSchoolCard
                    key={s.id || i}
                    school={s}
                    rank={null}
                    currentGrade={summary.grade}
                    neutralReason={nearMissReason(s, summary.budget)}
                    reportId={reportId}
                    apiUrl={API_URL}
                    summary={summary}
                    isNearMiss
                  />
                ) : (
                  <BlurredSchoolCard
                    key={s.id || i}
                    index={null}
                    teaser={teaserLine(s, i, summary.location_preference)}
                  />
                )
              ))}
            </div>
          </section>
        )}

        {/* Post-unlock: PDF/Email slot (Phase 2) */}
        {unlocked && (
          <section className="mt-10 rounded-2xl bg-white border border-[#E8DED3] p-6 text-center" data-testid="post-unlock-actions">
            <h3 className="text-lg font-bold text-[#2C2823] mb-2">Take this with you</h3>
            <p className="text-sm text-[#6B6560] mb-4">
              PDF download and email delivery are coming soon — we're finishing up the report formatting.
            </p>
            <button
              type="button"
              disabled
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#F5F3ED] text-[#9B8F88] font-semibold cursor-not-allowed"
              data-testid="pdf-download-btn-disabled"
            >
              Download PDF <ChevronRight className="w-4 h-4" />
            </button>
          </section>
        )}
      </div>
    </div>
  );
};

export default ParentReportPage;
